import { onMounted, onUnmounted } from "vue";
import { SYSTEM_DARK_QUERY, useThemeStore } from "~/stores/themeStore";

/**
 * Keeps `themeStore.osPrefersDark` level with the operating system for as long
 * as the app is on screen.
 *
 * The store already reads the OS once, when it is created, so the first paint
 * is correct without this. What this adds is the live half: someone on the
 * "System" setting whose machine switches to dark at sunset should watch the
 * app follow, in place, without reloading. `matchMedia` fires `change` for
 * exactly that, and it fires for the OS-level switch as well as for the browser
 * being told to override it.
 *
 * Call it once, from the app shell. A second caller would be harmless — the
 * listeners are independent and write the same value — but pointless.
 *
 * The listener is removed on unmount. `matchMedia` results are held by the
 * browser rather than garbage-collected with the component, so a listener left
 * behind would keep writing into a store after its owner had gone.
 */
export function useSystemThemeSync() {
  const theme = useThemeStore();

  let mql: MediaQueryList | null = null;

  const update = (event: MediaQueryListEvent | MediaQueryList) => {
    theme.syncSystemPreference(event.matches);
  };

  onMounted(() => {
    // This app runs with `ssr: false`, so `window` is here. The guard covers
    // the test environment and very old browsers with no `matchMedia`, where
    // the store's seeded value simply stands for the session.
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }

    mql = window.matchMedia(SYSTEM_DARK_QUERY);
    // Re-read on mount rather than trusting the value the store seeded at
    // creation: the two happen at different moments, and the OS can change in
    // between — most obviously when the store was restored from a tab that had
    // been open since before the machine switched.
    update(mql);
    mql.addEventListener("change", update);
  });

  onUnmounted(() => {
    mql?.removeEventListener("change", update);
    mql = null;
  });
}
